import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Brand } from "../Brand";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import api from "../../lib/api";
import { toast } from "sonner";
import { MailCheck, ArrowLeft } from "lucide-react";

/**
 * Step 1 of password reset — request the reset link.
 *
 * Backend always returns 200 regardless of whether the email exists (account
 * enumeration defence), so we always show the same confirmation copy.
 *
 * Audience: `?audience=merchant` switches the endpoint to the XPay merchant
 * variant (different DB collection, different login redirect). Default is the
 * consumer flow.
 */
export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const audience = (params.get("audience") || "user").toLowerCase();
  const isMerchant = audience === "merchant";
  const endpoint = isMerchant ? "/xpay/merchant/forgot-password" : "/auth/forgot-password";
  const backTo = isMerchant ? "/pay" : "/login";
  const productLabel = isMerchant ? "Xchangely Pay" : "Xchangely";
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(endpoint, { email });
      setSubmitted(true);
    } catch (e) {
      toast.error("Couldn't send the reset link. Try again in a moment.");
    } finally { setLoading(false); }
  };

  return (
    <div
      className="min-h-screen bg-background flex items-start sm:items-center justify-center p-6 overflow-y-auto"
      style={{ minHeight: "100dvh", paddingBottom: "max(env(safe-area-inset-bottom), 6rem)" }}
    >
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <Brand size={48} className="mx-auto" />
        </div>
        <div className="xch-card p-6 space-y-4" data-testid="forgot-password-card">
          {!submitted ? (
            <>
              <div className="text-center">
                <h1 className="font-display text-2xl font-bold text-foreground">Reset your password</h1>
                <p className="text-sm text-muted-foreground mt-1.5">
                  Enter the email tied to your {productLabel} account. We&apos;ll send you a 30-minute link to set a new password.
                </p>
              </div>
              <form onSubmit={submit} className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Email</Label>
                  <Input required type="email" value={email} autoComplete="email"
                         onChange={(e) => setEmail(e.target.value)}
                         className="rounded-xl"
                         data-testid="forgot-email-input" />
                </div>
                <Button type="submit" disabled={loading || !email.includes("@")}
                        className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl h-12 font-display font-semibold"
                        data-testid="forgot-submit-btn">
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
              </form>
              <Link to={backTo}
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                    data-testid="forgot-back-to-login">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
              </Link>
            </>
          ) : (
            <div className="text-center space-y-3" data-testid="forgot-success">
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-700 mx-auto flex items-center justify-center">
                <MailCheck className="w-6 h-6" />
              </div>
              <h2 className="font-display text-xl font-bold text-foreground">Check your inbox</h2>
              <p className="text-sm text-muted-foreground">
                If <strong>{email}</strong> matches an account with us, you&apos;ll receive a reset link within a minute. It expires in 30 minutes.
              </p>
              <p className="text-xs text-muted-foreground">
                Didn&apos;t get it? Check spam, or wait a minute and try again.
              </p>
              <Button onClick={() => navigate(backTo)} variant="outline" className="rounded-xl mt-2">
                Back to sign in
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
